<template>
  <el-dialog 
    title="选择销售员" 
    :visible.sync="dialogVisible" 
    width="400px" 
    append-to-body
    @close="handleClose" 
    @opened="handleOpened"
    @keydown="handleDialogKeyDown"
  >
    <!-- 商品信息展示 -->
    <div 
      v-if="selectedCartItem"
      style="margin-bottom: 15px; padding: 10px; background: #f0f9ff; border-radius: 4px; border-left: 4px solid #409EFF;"
    >
      <div style="color: #409EFF; font-weight: bold; margin-bottom: 5px;">商品信息：</div>
      <div style="color: #666; font-size: 14px;">
        {{ selectedCartItem.bgmName }} - {{ selectedCartItem.spec }}
      </div>
      <div style="color: #666; font-size: 14px;">
        当前销售员：{{ selectedCartItem.salesPersonName || '未设置' }} 
      </div>
    </div>

    <!-- 销售员选择表单 -->
    <el-form 
      id="salesPersonSelectForm" 
      :model="form" 
      :rules="rules" 
      ref="salesPersonSelectFormRef"
      label-width="80px" 
      size="small" 
      @keydown="handleFormKeyDown"
    >
      <el-form-item label="销售员" prop="salesPerson">
        <el-select 
          ref="salesPersonSelect" 
          v-model="form.salesPerson" 
          placeholder="请选择销售员"
          style="width: 100%" 
          @keydown.native="handleSelectKeyDown"
          @visible-change="onSelectVisibleChange"
          @change="handleSalesPersonChange"
        >
          <el-option 
            v-for="salesPerson in salesPersonList" 
            :key="salesPerson.opmId" 
            :label="salesPerson.opmName"
            :value="salesPerson.opmId" 
          />
        </el-select>
      </el-form-item>
    </el-form>

    <!-- 底部按钮 -->
    <span slot="footer" class="dialog-footer">
      <el-button 
        ref="salesPersonConfirmButton" 
        type="primary" 
        @click="handleConfirm"
        @keydown.enter.native="handleConfirm"
      >
        确认
      </el-button>
    </span>
  </el-dialog>
</template>

<script>
export default {
  name: 'SalesPersonSelectDialog',
  props: {
    // 弹窗显示状态
    visible: {
      type: Boolean,
      default: false
    },
    // 选中的购物车商品
    selectedCartItem: {
      type: Object,
      default: () => null
    },
    // 销售员列表
    salesPersonList: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      // 表单数据
      form: {
        salesPerson: '',
        salesPersonName: ''
      },
      // 下拉框是否已关闭
      selectClosed: false
    };
  },
  computed: {
    // 弹窗显示状态的双向绑定
    dialogVisible: {
      get() {
        return this.visible;
      },
      set(value) {
        this.$emit('update:visible', value);
      }
    },
    
    // 表单验证规则
    rules() {
      return {
        salesPerson: [
          { required: true, message: '请选择销售员', trigger: 'blur' }
        ]
      };
    }
  },
  watch: {
    // 监听选中商品变化，更新表单数据
    selectedCartItem: {
      handler(newItem) {
        if (newItem) {
          this.form.salesPerson = newItem.salesPerson || '';
          this.form.salesPersonName = newItem.salesPersonName || '';
        }
      },
      immediate: true
    }
  },
  methods: {
    // 弹窗打开事件
    handleOpened() {
      this.selectClosed = false; // 重置状态
      this.$nextTick(() => {
        if (this.$refs.salesPersonSelect) {
          this.$refs.salesPersonSelect.focus();
        }

        // 添加全局ESC键监听，使用捕获阶段确保最先处理
        document.addEventListener('keydown', this.handleGlobalEsc, true);

        // 为销售员选择下拉框添加特殊的ESC键处理
        const selectEl = this.$refs.salesPersonSelect.$el;
        if (selectEl) {
          selectEl.addEventListener('keydown', (event) => {
            if (event.key === 'Escape') {
              console.log('销售员下拉框ESC键被触发');
              event.stopPropagation();
              event.preventDefault();
              this.dialogVisible = false;
              this.$nextTick(() => {
                this.$emit('focus-cart');
              });
            }
          }, true);
        }
      });
      this.$emit('opened');
    },

    // 弹窗关闭事件
    handleClose() {
      // 先关闭下拉框
      if (this.$refs.salesPersonSelect) {
        // 尝试关闭下拉框
        this.$refs.salesPersonSelect.blur();
        // 如果有visible属性，设置为false
        if (this.$refs.salesPersonSelect.visible !== undefined) {
          this.$refs.salesPersonSelect.visible = false;
        }
      }

      this.dialogVisible = false;

      // 移除全局ESC键监听（注意第三个参数需要与添加时一致）
      document.removeEventListener('keydown', this.handleGlobalEsc, true);

      // 移除销售员下拉框的ESC键监听
      if (this.$refs.salesPersonSelect && this.$refs.salesPersonSelect.$el) {
        const selectEl = this.$refs.salesPersonSelect.$el;
        // 由于无法保存原始的事件处理函数引用，这里使用一个空函数来移除所有键盘事件
        selectEl.onkeydown = null;
      }

      this.$emit('close');
    },

    // 处理销售员选择变化
    handleSalesPersonChange(selectedId) {
      // 根据选中的ID找到对应的销售员名称
      const selectedSalesPerson = this.salesPersonList.find(person => person.opmId === selectedId);
      if (selectedSalesPerson) {
        this.form.salesPersonName = selectedSalesPerson.opmName;
      }
      console.log('销售员选择变化:', selectedId, this.form.salesPersonName);
    },

    // 确认选择
    handleConfirm() {
      this.$refs.salesPersonSelectFormRef.validate((valid) => {
        if (!valid) return;
        
        const salesPerson = this.form.salesPerson;
        const salesPersonName = this.form.salesPersonName;
        console.log('handleConfirm', salesPerson, salesPersonName);
        
        this.$emit('confirm', { salesPerson, salesPersonName });
      });
    },

    // 全局ESC键处理
    handleGlobalEsc(event) {
      // 确保事件是从销售员选择弹窗内部触发的
      if (event.key === 'Escape' && this.dialogVisible) {
        console.log('全局ESC键被触发，关闭销售员选择弹窗');
        event.preventDefault();
        event.stopPropagation();

        // 先关闭下拉框
        if (this.$refs.salesPersonSelect) {
          this.$refs.salesPersonSelect.blur();
          if (this.$refs.salesPersonSelect.visible !== undefined) {
            this.$refs.salesPersonSelect.visible = false;
          }
        }

        this.dialogVisible = false;
        // 移除监听器
        document.removeEventListener('keydown', this.handleGlobalEsc, true);
        // 聚焦回购物车
        this.$nextTick(() => {
          this.$emit('focus-cart');
        });
      }
    },

    // 处理弹窗的键盘事件
    handleDialogKeyDown(event) {
      // 可以在这里添加其他弹窗级别的键盘事件处理
    },

    // 处理表单的键盘事件
    handleFormKeyDown(event) {
      console.log('handleFormKeyDown', event);
      if (event.key === 'Escape') {
        console.log('销售员表单ESC键被触发');
        event.preventDefault();
        event.stopPropagation();

        // 先关闭下拉框
        if (this.$refs.salesPersonSelect) {
          this.$refs.salesPersonSelect.blur();
          if (this.$refs.salesPersonSelect.visible !== undefined) {
            this.$refs.salesPersonSelect.visible = false;
          }
        }

        this.dialogVisible = false;
        // 聚焦回购物车
        this.$nextTick(() => {
          this.$emit('focus-cart');
        });
      }
    },

    // 销售员选择键盘事件处理
    handleSelectKeyDown(event) {
      if (!this.dialogVisible) {
        return;
      }

      console.log(event.key, 'event.key');
      // 处理回车键
      if (event.key === 'Enter') {
        // 检查下拉框是否已关闭
        if (this.selectClosed) {
          // 如果下拉框已关闭，触发确认
          event.preventDefault();
          event.stopPropagation();
          this.handleConfirm();
          return;
        }
        // 如果下拉框未关闭，让下拉框自己处理回车键选择
        return;
      } else if (event.key === 'Escape') {
        console.log('销售员选择下拉框ESC键被触发');
        event.preventDefault();
        event.stopPropagation();

        // 先关闭下拉框
        if (this.$refs.salesPersonSelect) {
          this.$refs.salesPersonSelect.blur();
          if (this.$refs.salesPersonSelect.visible !== undefined) {
            this.$refs.salesPersonSelect.visible = false;
          }
        }

        // 关闭销售员选择弹窗
        this.dialogVisible = false;
        // 聚焦回购物车
        this.$nextTick(() => {
          this.$emit('focus-cart');
        });
        return;
      }
    },

    // 销售员选择下拉框可见性变化处理
    onSelectVisibleChange(visible) {
      if (!visible) {
        // 下拉框关闭后，延迟一下再允许回车确认
        this.$nextTick(() => {
          this.selectClosed = true;
        });
      } else {
        this.selectClosed = false;
      }
    }
  }
};
</script>

<style scoped>

::v-deep #salesPersonSelectForm .el-form-item__label {
  color: #606266 !important;
}
</style>
